import React, { createContext, useContext } from 'react';
import {
  View as PdfView, Text as PdfText, Image as PdfImage,
  Svg as PdfSvg, Path as PdfPath, Rect as PdfRect, Circle as PdfCircle,
  Defs as PdfDefs, LinearGradient as PdfLinearGradient, Stop as PdfStop,
} from '@react-pdf/renderer';
import { pdfFontFamily } from '../../../utils/pdf';

/**
 * The building blocks every ID card is drawn with. Each renders through
 * @react-pdf when the card is printed and as plain HTML in the on-screen
 * preview, from the same styles — so the preview is exactly what prints.
 *
 * Styles are react-pdf styles: numbers are points, and every View is a
 * flex column. The HTML side reproduces that (1pt = 1px, scaled by the
 * preview), so only the subset below is used on cards: flexbox, sizes,
 * padding, borders, radii, colours, text and absolute positioning.
 */

type Target = 'pdf' | 'html';
// PDF is the default: react-pdf renders with its own reconciler, which does
// not see context set above it, so only the on-screen preview — rendered by
// React DOM — switches to HTML through <CardTarget target="html">.
const TargetContext = createContext<Target>('pdf');

export const CardTarget: React.FC<{ target: Target; children: React.ReactNode }> = ({ target, children }) => (
  <TargetContext.Provider value={target}>{children}</TargetContext.Provider>
);

export type CardStyle = Record<string, any>;
type StyleProp = CardStyle | Array<CardStyle | false | null | undefined> | undefined;

const flat = (style: StyleProp): CardStyle =>
  Array.isArray(style) ? Object.assign({}, ...style.filter(Boolean)) : { ...(style ?? {}) };

/** react-pdf's axis shorthands, which CSS doesn't have. */
const AXES: Record<string, [string, string]> = {
  paddingHorizontal: ['paddingLeft', 'paddingRight'],
  paddingVertical: ['paddingTop', 'paddingBottom'],
  marginHorizontal: ['marginLeft', 'marginRight'],
  marginVertical: ['marginTop', 'marginBottom'],
};

/** A react-pdf style as CSS: axis shorthands expanded, and the card fonts
 *  named as the browser knows them (see IdCardPreview). */
const css = (s: CardStyle): CardStyle => {
  const out: CardStyle = {};
  Object.entries(s).forEach(([k, v]) => {
    if (AXES[k]) { out[AXES[k][0]] = v; out[AXES[k][1]] = v; } else out[k] = v;
  });
  if (out.fontFamily === pdfFontFamily.mono) out.fontFamily = `"${pdfFontFamily.mono}", ui-monospace, monospace`;
  else if (out.fontFamily === pdfFontFamily.sans) out.fontFamily = `"${pdfFontFamily.sans}", sans-serif`;
  return out;
};

export const V: React.FC<{ style?: StyleProp; children?: React.ReactNode }> = ({ style, children }) => {
  const target = useContext(TargetContext);
  if (target === 'pdf') return <PdfView style={flat(style)}>{children}</PdfView>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', boxSizing: 'border-box', flexShrink: 0, ...css(flat(style)) }}>
      {children}
    </div>
  );
};

/** Line height a text uses unless its style sets one. Always set on the
 *  text itself: react-pdf resolves an inherited line height against the
 *  parent's font size, so a small label under a large default would gape. */
const LINE_HEIGHT = 1.2;

/** Text. `lines` caps it to that many lines, ending in an ellipsis. Texts
 *  never nest: react-pdf mis-measures nested text of mixed sizes, so a line
 *  of mixed styles is a row of texts instead (see Validity). */
export const T: React.FC<{ style?: StyleProp; lines?: number; children?: React.ReactNode }> = ({ style, lines, children }) => {
  const target = useContext(TargetContext);
  const s = { lineHeight: LINE_HEIGHT, ...flat(style) };
  if (target === 'pdf') {
    return <PdfText style={lines ? { ...s, maxLines: lines, textOverflow: 'ellipsis' } : s}>{children}</PdfText>;
  }
  const clamp: CardStyle = lines
    ? { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }
    : {};
  return <div style={{ margin: 0, ...css(s), ...clamp }}>{children}</div>;
};

export const Img: React.FC<{ src: string; style?: StyleProp }> = ({ src, style }) => {
  const target = useContext(TargetContext);
  const s = flat(style);
  if (target === 'pdf') return <PdfImage src={src} style={s} />;
  return <img src={src} alt="" style={{ display: 'block', flexShrink: 0, ...css(s) }} />;
};

// ── Vector shapes, for the card artwork ─────────────────────────────────────

type SvgProps = { width: number; height: number; viewBox?: string; style?: StyleProp; children?: React.ReactNode };
export const Svg: React.FC<SvgProps> = ({ style, children, ...rest }) => {
  const target = useContext(TargetContext);
  if (target === 'pdf') return <PdfSvg {...rest} style={flat(style)}>{children}</PdfSvg>;
  return <svg {...rest} style={{ display: 'block', flexShrink: 0, ...css(flat(style)) }}>{children}</svg>;
};

/** One SVG element under both renderers — the props are the same. */
const shape = <P extends object>(Pdf: React.ComponentType<P>, tag: string): React.FC<P & { children?: React.ReactNode }> =>
  (props) => {
    const target = useContext(TargetContext);
    return target === 'pdf' ? <Pdf {...props} /> : React.createElement(tag, props);
  };

export const Path = shape<{ d: string; fill?: string; opacity?: number; fillOpacity?: number }>(PdfPath as any, 'path');
export const Rect = shape<{ x?: number; y?: number; width: number; height: number; rx?: number; fill?: string; opacity?: number }>(PdfRect as any, 'rect');
export const Circle = shape<{ cx: number; cy: number; r: number; fill?: string; opacity?: number; stroke?: string; strokeWidth?: number }>(PdfCircle as any, 'circle');
export const Defs = shape<{}>(PdfDefs as any, 'defs');
export const LinearGradient = shape<{ id: string; x1: string | number; y1: string | number; x2: string | number; y2: string | number }>(PdfLinearGradient as any, 'linearGradient');
export const Stop = shape<{ offset: string | number; stopColor: string; stopOpacity?: number }>(PdfStop as any, 'stop');
